// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The identifier layer: does this DOI point at the paper the draft says it does?
 *
 * THIS IS THE ONLY PART OF THE TOOL THAT TOUCHES THE NETWORK, AND IT IS OFF
 * UNTIL THE USER TURNS IT ON. What it sends, when on, is the list of DOIs,
 * PMIDs and arXiv ids from the draft — nothing else. Not the quotations, not the
 * draft prose, not one byte of a source file. That distinction is the whole
 * reason the switch can exist at all in a tool whose promise is that the files
 * stay put.
 *
 * All four registries were re-verified CORS-open from a third-party Origin on
 * 2026-08-21:
 *   api.crossref.org          access-control-allow-origin: *
 *   api.openalex.org          access-control-allow-origin: *
 *   api.datacite.org          echoes the requesting Origin
 *   eutils.ncbi.nlm.nih.gov   access-control-allow-origin: *
 *
 * There is no fifth. api.semanticscholar.org was named in the privacy copy and
 * granted in the CSP without a single call site ever existing; a privacy page
 * that names a host the tool never contacts is wrong in the direction that
 * costs trust, so the host was removed rather than a resolver invented for it.
 *
 * export.arxiv.org is NOT usable: it answers 200 with no access-control-* header
 * of any kind, and its OPTIONS preflight times out. arXiv ids are therefore
 * resolved through their DataCite DOI, 10.48550/arXiv.<id>, which carries the
 * identical title — verified end to end on 1706.03762 returning "Attention Is
 * All You Need".
 *
 * Requests are serialised at three per second. NCBI documents that as the
 * unauthenticated ceiling, and a burst of forty parallel lookups from a page
 * that promises to be well-behaved is exactly how a public registry decides to
 * block an origin.
 *
 * NOT REACHING A REGISTRY IS NOT A VERDICT ABOUT AN IDENTIFIER. Offline, a
 * captive portal, a corporate proxy, an ad-blocker sitting on eutils, one flaky
 * host — each makes fetch reject, and this module used to let that stand as
 * "unresolvable", which the interface paints in the same red as a quotation
 * that is nowhere in the sources. Every real DOI in the bibliography was
 * accused because the wifi was down. So a transport failure now raises
 * Unreachable, which is caught per registry (so one blocked host no longer
 * kills the fallback chain behind it) and, when nothing answered at all,
 * becomes the verdict 'not-checked' rather than a finding.
 */

import type { Identifier, IdentifierResult, IdentifierVerdict } from '../types.ts';

const MIN_INTERVAL_MS = 340;

export function extractIdentifiers(raw: string): Identifier[] {
  const found: Identifier[] = [];
  const seen = new Set<string>();

  const add = (kind: Identifier['kind'], value: string, start: number, rawText: string) => {
    const key = `${kind}:${value.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    found.push({ raw: rawText, kind, value, start, assertedTitle: assertedTitleNear(raw, start) });
  };

  // Trailing punctuation is part of the sentence, not of the DOI.
  const doiRe = /\b10\.\d{4,9}\/[-._;()/:A-Z0-9]+/gi;
  let m: RegExpExecArray | null;
  while ((m = doiRe.exec(raw)) !== null) {
    const value = m[0].replace(/[.,;:)\]]+$/, '');
    add('doi', value, m.index, m[0]);
  }

  const arxivRe = /\barxiv[:\s]\s*(\d{4}\.\d{4,5}(?:v\d+)?)\b/gi;
  while ((m = arxivRe.exec(raw)) !== null) add('arxiv', m[1], m.index, m[0]);

  const pmidRe = /\bpmid[:\s]\s*(\d{1,9})\b/gi;
  while ((m = pmidRe.exec(raw)) !== null) add('pmid', m[1], m.index, m[0]);

  return found.sort((a, b) => a.start - b.start);
}

/**
 * The title the draft asserts for an identifier.
 *
 * A reference entry puts the title before the identifier, so this looks
 * backwards from the identifier to the start of its line and takes the longest
 * quoted span, or the longest sentence-like run. It is a heuristic and it is
 * treated as one: when no title can be read, the verdict says the title was not
 * checked rather than claiming a match.
 */
function assertedTitleNear(raw: string, at: number): string | null {
  const lineStart = raw.lastIndexOf('\n', at) + 1;
  const line = raw.slice(lineStart, at);
  const quoted = [...line.matchAll(/["“']([^"”']{12,200})["”']/g)].map((q) => q[1]);
  if (quoted.length > 0) return quoted.sort((a, b) => b.length - a.length)[0].trim();
  // Reference styles separate fields with ". " — the title is usually the
  // longest such field that is not the author list.
  const fields = line
    .split(/\.\s+/)
    .map((f) => f.trim())
    .filter((f) => f.length >= 15 && /\s/.test(f));
  if (fields.length === 0) return null;
  return fields.sort((a, b) => b.length - a.length)[0].replace(/^[-–—*\s]+/, '').trim() || null;
}

interface Resolved {
  title: string | null;
  year: number | null;
  author: string | null;
  registry: string;
}

/**
 * null means "the registry answered, and it does not have this". Unreachable
 * means "I never got an answer" — the two must never collapse into each other.
 */
async function getJson(url: string, signal: AbortSignal): Promise<unknown | null> {
  let response: Response;
  try {
    response = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  } catch (error) {
    // The user aborted; that is not a network failure and must propagate.
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new Unreachable();
  }
  if (response.status === 429) throw new RateLimited();
  if (!response.ok) return null;
  try {
    return await response.json();
  } catch {
    // A 200 whose body is not JSON is an answer I cannot read, not a silence.
    // It counts as the registry not having the record.
    return null;
  }
}

class RateLimited extends Error {}
class Unreachable extends Error {}

/** What one registry lookup produced, and whether any host went unanswered. */
interface Attempt {
  resolved: Resolved | null;
  /** True when at least one registry never answered, so `null` proves nothing. */
  unreachable: boolean;
}

function firstString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
  if (Array.isArray(value) && value[0] && typeof value[0] === 'object') {
    const inner = (value[0] as Record<string, unknown>).title;
    if (typeof inner === 'string') return inner;
  }
  return null;
}

async function resolveDoi(doi: string, signal: AbortSignal): Promise<Attempt> {
  // Each registry is tried independently. Crossref being blocked used to throw
  // straight out of this function, which made the DataCite and OpenAlex
  // fallbacks below unreachable code the moment one host was — so an arXiv id
  // DataCite would have resolved perfectly came back as if it did not exist.
  let unreachable = false;
  const tryJson = async (url: string): Promise<unknown | null> => {
    try {
      return await getJson(url, signal);
    } catch (error) {
      if (error instanceof Unreachable) {
        unreachable = true;
        return null;
      }
      throw error;
    }
  };

  const crossref = (await tryJson(`https://api.crossref.org/works/${encodeURIComponent(doi)}`)) as
    | { message?: Record<string, unknown> }
    | null;
  const message = crossref?.message;
  if (message) {
    const parts = (message['published-print'] ?? message['published-online'] ?? message.issued) as
      | { 'date-parts'?: number[][] }
      | undefined;
    const authors = message.author as { family?: string }[] | undefined;
    return {
      resolved: {
        title: firstString(message.title),
        year: parts?.['date-parts']?.[0]?.[0] ?? null,
        author: authors?.[0]?.family ?? null,
        registry: 'api.crossref.org',
      },
      unreachable: false,
    };
  }

  const datacite = (await tryJson(`https://api.datacite.org/dois/${encodeURIComponent(doi)}`)) as
    | { data?: { attributes?: Record<string, unknown> } }
    | null;
  const attributes = datacite?.data?.attributes;
  if (attributes) {
    const creators = attributes.creators as { familyName?: string; name?: string }[] | undefined;
    return {
      resolved: {
        title: firstString(attributes.titles),
        year: typeof attributes.publicationYear === 'number' ? attributes.publicationYear : null,
        author: creators?.[0]?.familyName ?? creators?.[0]?.name ?? null,
        registry: 'api.datacite.org',
      },
      unreachable: false,
    };
  }

  const openalex = (await tryJson(`https://api.openalex.org/works/doi:${encodeURIComponent(doi)}`)) as
    | Record<string, unknown>
    | null;
  if (openalex && typeof openalex.title === 'string') {
    const authorships = openalex.authorships as { author?: { display_name?: string } }[] | undefined;
    return {
      resolved: {
        title: openalex.title,
        year: typeof openalex.publication_year === 'number' ? openalex.publication_year : null,
        author: authorships?.[0]?.author?.display_name ?? null,
        registry: 'api.openalex.org',
      },
      unreachable: false,
    };
  }
  // Nothing resolved. Whether that means "no registry has it" or "I never got
  // to ask" is the whole difference, and it rides out on this flag.
  return { resolved: null, unreachable };
}

async function resolvePmid(pmid: string, signal: AbortSignal): Promise<Attempt> {
  let data: { result?: Record<string, { title?: string; pubdate?: string; sortfirstauthor?: string }> } | null;
  try {
    data = (await getJson(
      `https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esummary.fcgi?db=pubmed&id=${encodeURIComponent(pmid)}&retmode=json`,
      signal,
    )) as { result?: Record<string, { title?: string; pubdate?: string; sortfirstauthor?: string }> } | null;
  } catch (error) {
    if (error instanceof Unreachable) return { resolved: null, unreachable: true };
    throw error;
  }
  const record = data?.result?.[pmid];
  if (!record?.title) return { resolved: null, unreachable: false };
  const year = /\b(\d{4})\b/.exec(record.pubdate ?? '');
  return {
    resolved: {
      title: record.title,
      year: year ? parseInt(year[1], 10) : null,
      author: record.sortfirstauthor ?? null,
      registry: 'eutils.ncbi.nlm.nih.gov',
    },
    unreachable: false,
  };
}

function normaliseTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/<[^>]*>/g, ' ')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Token-set overlap, which survives subtitle differences and reordering. */
export function titleSimilarity(a: string, b: string): number {
  const setA = new Set(normaliseTitle(a).split(' ').filter((w) => w.length > 2));
  const setB = new Set(normaliseTitle(b).split(' ').filter((w) => w.length > 2));
  if (setA.size === 0 || setB.size === 0) return 0;
  let shared = 0;
  for (const word of setA) if (setB.has(word)) shared++;
  return shared / Math.min(setA.size, setB.size);
}

export interface ResolveProgress {
  (done: number, total: number): void;
}

export async function resolveIdentifiers(
  identifiers: Identifier[],
  signal: AbortSignal,
  onProgress?: ResolveProgress,
): Promise<IdentifierResult[]> {
  const out: IdentifierResult[] = [];
  let last = 0;

  for (let i = 0; i < identifiers.length; i++) {
    const identifier = identifiers[i];
    const wait = MIN_INTERVAL_MS - (Date.now() - last);
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    last = Date.now();

    let attempt: Attempt = { resolved: null, unreachable: false };
    try {
      if (identifier.kind === 'doi') attempt = await resolveDoi(identifier.value, signal);
      else if (identifier.kind === 'pmid') attempt = await resolvePmid(identifier.value, signal);
      else if (identifier.kind === 'arxiv') {
        // export.arxiv.org sends no CORS header at all, so the id goes through
        // its DataCite DOI instead.
        attempt = await resolveDoi(`10.48550/arXiv.${identifier.value.replace(/v\d+$/, '')}`, signal);
      }
    } catch (error) {
      if (error instanceof RateLimited) {
        out.push({
          identifier,
          verdict: 'rate-limited',
          resolvedTitle: null,
          resolvedYear: null,
          resolvedAuthor: null,
          registry: null,
        });
        onProgress?.(i + 1, identifiers.length);
        continue;
      }
      if ((error as Error)?.name === 'AbortError') throw error;
      // Anything else that got this far is a failure I did not anticipate, and
      // an unanticipated failure is not evidence that a registry said no.
      attempt = { resolved: null, unreachable: true };
    }

    const resolved = attempt.resolved;
    // 'unresolvable' has to mean "every registry answered and none knew it".
    // If a host never answered, the honest verdict is that I did not check.
    let verdict: IdentifierVerdict = attempt.unreachable ? 'not-checked' : 'unresolvable';

    if (resolved?.title) {
      verdict = 'match';
      if (identifier.assertedTitle) {
        const similarity = titleSimilarity(identifier.assertedTitle, resolved.title);
        if (similarity < 0.5) verdict = 'title-mismatch';
      }
    }

    out.push({
      identifier,
      verdict,
      resolvedTitle: resolved?.title ?? null,
      resolvedYear: resolved?.year ?? null,
      resolvedAuthor: resolved?.author ?? null,
      registry: resolved?.registry ?? null,
    });
    onProgress?.(i + 1, identifiers.length);
  }

  return out;
}
